import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DwConfigService, DwConfigServiceToken, SuppressErrorMessagesHeader } from '@devwareapps/devware-cap';
import { Observable } from 'rxjs';
import { map, switchMap, take, timeout } from 'rxjs/operators';

export interface ProcessingChallenge {
    ChallengeDefinitionId: number; Name: string; EndDate?: string; Enabled: boolean; FinalRunId?: number;
    EligibleUtc?: string; PublishedRunId?: number; LastPublishedDateTime?: string; LastScoreThroughDate?: string;
}
export interface ProcessingOptions { reason: string; reviseFinals: boolean; provision: boolean; }
export interface ProcessingRow {
    challenge: ProcessingChallenge; operationId: string; state: 'waiting' | 'stats' | 'awards' | 'completed' | 'failed';
    message?: string; summariesUpdated?: number; outcome?: string; granted?: number; revoked?: number; unavailable?: string[];
}
interface StatsResult { ChallengeDefinitionId: number; Success: boolean; SummariesUpdated: number; StandingsUpdated: boolean; FailedUserChallengeIds: number[]; }
interface AwardResult { ChallengeDefinitionId: number; Outcome: string; FinalRunId?: number; Granted: number; Revoked: number; Unavailable: string[]; }

/** Application maintenance endpoints own authorization, calculation and publication. */
@Injectable({providedIn: 'root'})
export class ChallengeProcessingService {
    private readonly _headers = {[SuppressErrorMessagesHeader]: 'true'};
    constructor(private _http: HttpClient, @Inject(DwConfigServiceToken) private _config: DwConfigService) {}
    private get root(): string { return this._config.coreConfig.apiRoot.replace(/\/$/, ''); }
    candidates(): Observable<ProcessingChallenge[]> {
        return this._http.get<ProcessingChallenge[]>(this.root + '/fitness/gamification/awards/challenges', {headers: this._headers})
            .pipe(take(1), timeout(30000));
    }
    operationId(): string {
        // Use a secure UUID without adding a dependency or deriving it from dates.
        const bytes = new Uint8Array(16); window.crypto.getRandomValues(bytes);
        bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
        const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
        return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
    }
    process(row: ProcessingRow, options: ProcessingOptions): Observable<ProcessingRow> {
        row.state = 'stats';
        return this._http.post<StatsResult>(this.root + '/fitness/stats/maintenance/challenges/' + row.challenge.ChallengeDefinitionId + '/recalculate', {}, {headers: this._headers})
            .pipe(take(1), timeout(1800000), switchMap(stats => {
                if (stats.ChallengeDefinitionId !== row.challenge.ChallengeDefinitionId || !stats.Success || !stats.StandingsUpdated || stats.FailedUserChallengeIds?.length) {
                    throw new Error('Stats rebuild did not complete. Review the maintenance log before starting another run.');
                }
                row.summariesUpdated = stats.SummariesUpdated; row.state = 'awards';
                const action = options.reviseFinals && row.challenge.FinalRunId ? 'reprocess' : 'process';
                const historical = !!row.challenge.EligibleUtc && Date.parse(row.challenge.EligibleUtc) <= Date.now();
                return this._http.post<AwardResult>(this.root + '/fitness/gamification/awards/challenges/' + row.challenge.ChallengeDefinitionId + '/' + action,
                    {OperationId: row.operationId, Reason: options.reason.trim(), Provision: options.provision, Historical: historical}, {headers: this._headers})
                    .pipe(take(1), timeout(1800000));
            }), map(awards => {
                if (awards.ChallengeDefinitionId !== row.challenge.ChallengeDefinitionId
                    || !['finalized', 'already-finalized', 'already-processed', 'updated', 'deferred'].includes(awards.Outcome)
                    || (['finalized', 'already-finalized', 'already-processed'].includes(awards.Outcome) && !awards.FinalRunId)) {
                    throw new Error('The server did not confirm the processing result. Review the maintenance log before retrying.');
                }
                row.state = 'completed'; row.outcome = awards.Outcome; row.granted = awards.Granted || 0;
                row.revoked = awards.Revoked || 0; row.unavailable = awards.Unavailable || [];
                row.message = awards.Outcome === 'already-finalized' ? 'Stats refreshed; final awards preserved.'
                    : awards.Outcome === 'deferred' ? 'Stats refreshed; final-award correction deferred until the finalization window.'
                    : awards.Outcome === 'finalized' ? 'Stats and final awards published.' : 'Stats and awards processed.';
                return row;
            }));
    }
}
